import { TextField } from '@mui/material'
import { memo, useRef, useEffect, useMemo, useCallback, useState } from 'react'
import { getFieldId } from '../utils/utils'
import FieldHistory from '../../../components/DataEntry/FieldHistory.js'
import { FIELD_TYPES } from '../../../utility/constants'

const TextOrNumberField = ({
  id,
  field,
  value,
  onChange,
  type,
  style = {},
  className = '',
  label = null,
  placeholder = null,
  helperText = null,
  disabled = false,
  autoFocus = false,
  allValues,
  processId,
  inputSx = {},
}) => {
  const inputRef = useRef(null)

  const isNumberType = useMemo(() => {
    return (
      type === FIELD_TYPES.NUMBER ||
      field?.data_structure === FIELD_TYPES.NUMBER ||
      type === 'number'
    )
  }, [type, field?.data_structure])

  const decimalPlaces = useMemo(() => {
    if (!isNumberType) return null
    if (typeof field?.num_decimal_places == 'number') return field.num_decimal_places
    if (field?.num_decimal_places == 0) return 0
    return null
  }, [isNumberType, field?.num_decimal_places])

  // const preserveLeadingZeros = useMemo(() => {
  //   return Boolean(field?.metadata?.preserve_leading_zeros)
  // }, [field?.metadata])
  const preserveLeadingZeros = true

  const [displayValue, setDisplayValue] = useState(() => {
    if (!isNumberType) return value ?? ''
    return value === null || value === undefined ? '' : String(value)
  })

  const stepValue = useMemo(() => {
    if (!isNumberType) return undefined
    if (decimalPlaces === null) return 'any'
    if (decimalPlaces == 0) return 1
    return Number(`0.${'0'.repeat(Math.max(0, decimalPlaces - 1))}1`)
  }, [isNumberType, decimalPlaces])

  const inputProps = useMemo(() => {
    if (!isNumberType) return {}
    const allowNegative = true
    const neg = allowNegative ? '[-]?' : ''
    const pattern =
      decimalPlaces === null
        ? `^${neg}\\d*(?:[\\.,]\\d*)?$`
        : decimalPlaces == 0
          ? `^${neg}\\d*$`
          : `^${neg}\\d*(?:[\\.,]\\d{0,${decimalPlaces}})?$`
    return {
      step: stepValue,
      inputMode: 'numeric',
      pattern,
    }
  }, [isNumberType, stepValue, decimalPlaces, preserveLeadingZeros])

  const sanitizeIntPart = useCallback(
    (intPart) => {
      if (preserveLeadingZeros) return intPart
      if (!intPart) return '0'
      const isNeg = intPart.startsWith('-')
      let digits = isNeg ? intPart.slice(1) : intPart
      digits = digits.replace(/^0+(?=\d)/, '')
      if (digits === '') digits = '0'
      return isNeg ? `-${digits}` : digits
    },
    [preserveLeadingZeros],
  )

  const clampToDecimalPlaces = useCallback(
    (raw) => {
      if (!isNumberType || raw == null) return raw
      if (raw === '') return ''

      let valueString = String(raw).replace(',', '.')
      // Remove invalid characters early (always allow '-'; we will normalize to a single leading minus)
      valueString = valueString.replace(/[^0-9.\-]/g, '')

      if (valueString.includes('-')) {
        const isNegative = valueString.trim().startsWith('-')
        valueString = valueString.replace(/-/g, '')
        if (isNegative) valueString = '-' + valueString
      }

      const dot = valueString.indexOf('.')

      if (decimalPlaces == 0) {
        return dot !== -1
          ? sanitizeIntPart(valueString.slice(0, dot))
          : sanitizeIntPart(valueString)
      }

      if (decimalPlaces === null) {
        if (dot !== -1) {
          const intPart = sanitizeIntPart(valueString.slice(0, dot) || '0')
          const fracRest = valueString.slice(dot + 1).replace(/\./g, '')
          return `${intPart}.${fracRest}`
        }
        return preserveLeadingZeros ? valueString : sanitizeIntPart(valueString)
      }

      if (dot === -1) {
        return preserveLeadingZeros ? valueString : sanitizeIntPart(valueString)
      }

      let intPart = valueString.slice(0, dot)
      intPart = sanitizeIntPart(intPart || '0')
      let fracPart = valueString.slice(dot + 1).replace(/\./g, '')
      if (fracPart.length > decimalPlaces) {
        fracPart = fracPart.slice(0, decimalPlaces)
      }
      return `${intPart}.${fracPart}`
    },
    [isNumberType, decimalPlaces, preserveLeadingZeros, sanitizeIntPart],
  )

  const handleKeyDown = useCallback(
    (e) => {
      if (!isNumberType) return
      const navKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Home',
        'End',
        'Enter',
      ]
      const isCtrlCombo = e.ctrlKey || e.metaKey
      if (
        navKeys.includes(e.key) ||
        (isCtrlCombo && ['a', 'c', 'v', 'x', 'A', 'C', 'V', 'X'].includes(e.key))
      ) {
        return
      }
      const isDigit = /^[0-9]$/.test(e.key)
      const hasDot = (displayValue ?? '').includes('.')

      if (preserveLeadingZeros) {
        if (e.key === '-') {
          const cur = displayValue ?? ''
          if (cur.includes('-')) {
            e.preventDefault()
            return
          }
          e.preventDefault()
          const next = '-' + cur.replace(/-/g, '')
          setDisplayValue(next)
          onChange && onChange({ target: { value: next } })
          return
        }
        if (decimalPlaces === 0) {
          if (!isDigit) e.preventDefault()
        } else {
          if (e.key === '.') {
            if (hasDot) e.preventDefault()
          } else if (!isDigit) {
            e.preventDefault()
          }
        }
        return
      }

      // Block scientific notation characters for number inputs
      if (['e', 'E', '+'].includes(e.key)) {
        e.preventDefault()
        return
      }
      if (e.key === '.') {
        if (decimalPlaces === 0 || hasDot) e.preventDefault()
        return
      }
      if (e.key === '-') {
        const cur = displayValue ?? ''
        // If minus already exists, block
        if (cur.includes('-')) {
          e.preventDefault()
          return
        }
        // Prepend minus to the start regardless of caret position
        e.preventDefault()
        const next = '-' + cur.replace(/-/g, '')
        setDisplayValue(next)
        onChange && onChange({ target: { value: next } })
        return
      }
      if (!isDigit) e.preventDefault()
    },
    [isNumberType, preserveLeadingZeros, decimalPlaces, displayValue],
  )

  const handleChange = useCallback(
    (e) => {
      if (!isNumberType) {
        onChange && onChange(e)
        return
      }
      const trimmed = clampToDecimalPlaces(e?.target?.value ?? '')
      setDisplayValue(trimmed)
      const patchedEvent = { target: { value: trimmed } }
      onChange && onChange(patchedEvent)
    },
    [isNumberType, onChange, clampToDecimalPlaces],
  )

  const handleFocus = useCallback(() => {
    if (!isNumberType) return
    const currentVal = displayValue ?? ''
    if (!preserveLeadingZeros && currentVal == '0' && inputRef.current) {
      inputRef.current.select()
    }
  }, [isNumberType, preserveLeadingZeros, displayValue])

  useEffect(() => {
    if (!isNumberType) return
    const nextValue = value === null || value === undefined ? '' : String(value)
    setDisplayValue(nextValue)
  }, [value, isNumberType])

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  const handleMouseDown = (event) => {
    event.preventDefault()
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus()
      }, 50)
    }
  }

  const handleClick = (event) => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus()
      }, 50)
    }
  }

  const effectiveType = useMemo(() => {
    if (!isNumberType) return type
    return preserveLeadingZeros ? 'text' : type
  }, [isNumberType, preserveLeadingZeros, type])

  const resolvedValue = isNumberType ? displayValue : (value ?? '')

  if (!field || field.is_subfield) {
    return (
      <TextField
        type={effectiveType}
        id={getFieldId(id)}
        key={getFieldId(id)}
        name={getFieldId(id)}
        value={resolvedValue}
        autoFocus={autoFocus}
        fullWidth
        style={style}
        className={className}
        size="small"
        variant="outlined"
        label={label}
        color="primary"
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        error={helperText != null}
        inputRef={inputRef}
        disabled={disabled}
        sx={inputSx}
        inputProps={inputProps}
      />
    )
  } else {
    return (
      <FieldHistory
        fieldId={field?.id}
        field={field}
        processId={processId}
        allValues={allValues}
        helperText={helperText}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <TextField
          type={effectiveType}
          id={getFieldId(id)}
          key={getFieldId(id)}
          name={getFieldId(id)}
          value={resolvedValue}
          autoFocus={autoFocus}
          fullWidth
          style={style}
          className={className}
          size="small"
          variant="outlined"
          label={label}
          color="primary"
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          error={helperText != null}
          inputRef={inputRef}
          disabled={disabled}
          sx={inputSx}
          inputProps={inputProps}
        />
      </FieldHistory>
    )
  }
}

export default memo(
  TextOrNumberField,
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.disabled === nextProps.disabled,
)
